import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import { platformAPI } from '@/utils/api';
import { normalizePlatformCode, shopTypeLabel } from '@/utils/platformShopCatalog';
import { formatShopListName } from '@/pages/ipe/workbench/utils/platformShopManageUtils';
import { showToast } from '@/utils/toast';
import '@/styles/ipe/ipe-auth.css';

/**
 * TikTok OAuth Redirect 落地页。
 * Partner Center / 无 state 回调：/ipe/tiktok/auth-code?code=...
 * 登录后选择未授权店铺，确认后将 code 换票并同步店铺信息。
 */
function readQueryValue(searchParams, ...keys) {
  for (const key of keys) {
    const raw = String(searchParams.get(key) || '').trim();
    if (raw && raw !== 'null') return raw;
  }
  return '';
}

function isUnauthorizedTiktokShop(shop) {
  if (!shop || normalizePlatformCode(shop.platformCode) !== 'TIKTOK') return false;
  if (shop.mockBinding) return true;
  return String(shop.authStatus || '').toLowerCase() !== 'connected';
}

function shopDisplayName(shop) {
  return formatShopListName(shop?.shopName, shop?.platformCode)
    || String(shop?.customShopName || '').trim()
    || String(shop?.id ?? '');
}

export default function TiktokAuthCodePage() {
  const { t } = useTranslation('workbench');
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, bootstrapping, goToLoginPage } = useIpeAuth();

  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [shopsError, setShopsError] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const code = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return readQueryValue(sp, 'code', 'auth_code');
  }, [location.search]);

  const error = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return readQueryValue(sp, 'error_description', 'error');
  }, [location.search]);

  const unauthorizedShops = useMemo(
    () => (Array.isArray(shops) ? shops : []).filter(isUnauthorizedTiktokShop),
    [shops],
  );

  const loadShops = useCallback(async () => {
    if (!isAuthenticated) return;
    setShopsLoading(true);
    setShopsError('');
    try {
      const list = await platformAPI.listShops();
      const rows = Array.isArray(list) ? list : [];
      setShops(rows);
      const candidates = rows.filter(isUnauthorizedTiktokShop);
      setSelectedShopId((prev) => {
        if (prev && candidates.some((s) => String(s.id) === String(prev))) return prev;
        return candidates.length === 1 ? String(candidates[0].id) : '';
      });
    } catch (e) {
      setShops([]);
      setShopsError(String(e?.message || e?.rawMessage || t('shop.loadShopsFailed')));
    } finally {
      setShopsLoading(false);
    }
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (bootstrapping || !isAuthenticated || !code || error) return undefined;
    loadShops();
    return undefined;
  }, [bootstrapping, isAuthenticated, code, error, loadShops]);

  const onLogin = useCallback(() => {
    const redirect = `${location.pathname}${location.search || ''}`;
    goToLoginPage(redirect);
  }, [goToLoginPage, location.pathname, location.search]);

  const onConfirm = useCallback(async () => {
    const authCode = String(code || '').trim();
    const shopId = String(selectedShopId || '').trim();
    if (!authCode || !shopId || submitting) return;
    setSubmitting(true);
    try {
      await platformAPI.exchangeTiktokAuthCode(shopId, {
        authCode,
        auth_code: authCode,
        code: authCode,
      });
      try {
        await platformAPI.syncTiktokShopProfile(shopId);
      } catch {
        /* 换票已成功；资料同步失败不阻断 */
      }
      showToast(t('shop.authCodeOk'), 'success');
      const qs = new URLSearchParams({
        activeNav: 'platformShops',
        platformOAuth: 'ok',
        platformOAuthPlatform: 'TIKTOK',
        platformOAuthShopId: shopId,
      });
      navigate(`${IPE_WORKBENCH_PATH}?${qs.toString()}`, { replace: true });
    } catch (e) {
      const msg = String(e?.message || e?.rawMessage || t('shop.authCodeFailed'));
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }, [code, navigate, selectedShopId, submitting, t]);

  const manageHref = `${IPE_WORKBENCH_PATH}?activeNav=platformShops`;

  return (
    <div className="ipe-login-page" style={{ minHeight: '100vh' }}>
      <div className="ipe-login-page__panel" style={{ maxWidth: 560, margin: '12vh auto', padding: 32 }}>
        <IpeBrandMark
          className="ipe-login-page__brand"
          textClassName="ipe-login-page__brand-text"
          text="IPE"
        />
        <h1 style={{ fontSize: 22, margin: '24px 0 8px' }}>{t('shop.authCodePageTitle')}</h1>

        {error ? (
          <p style={{ color: 'var(--danger, #c0392b)', marginBottom: 0, lineHeight: 1.5 }}>
            {t('shop.authCodePageFailed', { error })}
          </p>
        ) : null}

        {!error && !code ? (
          <p style={{ color: 'var(--text-muted, #666)', marginBottom: 0, lineHeight: 1.5 }}>
            {t('shop.authCodePageEmpty')}
          </p>
        ) : null}

        {!error && code ? (
          <>
            <p style={{ color: 'var(--text-muted, #666)', marginBottom: 16, lineHeight: 1.5 }}>
              <Trans
                i18nKey="shop.authCodePageHint"
                ns="workbench"
                components={{ strong: <strong /> }}
              />
            </p>

            {bootstrapping ? (
              <p style={{ color: 'var(--text-muted, #666)', marginBottom: 0 }}>
                {t('shop.authCodePageLoadingShops')}
              </p>
            ) : !isAuthenticated ? (
              <>
                <p style={{ color: 'var(--text-muted, #666)', marginBottom: 16, lineHeight: 1.5 }}>
                  {t('shop.authCodePageLoginRequired')}
                </p>
                <button
                  type="button"
                  className="funding-button funding-button--primary"
                  onClick={onLogin}
                  style={{ minWidth: 120 }}
                >
                  {t('shop.authCodePageLogin')}
                </button>
              </>
            ) : shopsLoading ? (
              <p style={{ color: 'var(--text-muted, #666)', marginBottom: 0 }}>
                {t('shop.authCodePageLoadingShops')}
              </p>
            ) : shopsError ? (
              <>
                <p style={{ color: 'var(--danger, #c0392b)', marginBottom: 16, lineHeight: 1.5 }}>
                  {shopsError}
                </p>
                <button
                  type="button"
                  className="funding-button funding-button--ghost"
                  onClick={loadShops}
                  style={{ minWidth: 120 }}
                >
                  {t('shop.authCodePageRetry')}
                </button>
              </>
            ) : unauthorizedShops.length === 0 ? (
              <>
                <p style={{ color: 'var(--text-muted, #666)', marginBottom: 16, lineHeight: 1.5 }}>
                  {t('shop.authCodePageNoUnauthorized')}
                </p>
                <Link
                  to={manageHref}
                  className="funding-button funding-button--primary"
                  style={{ display: 'inline-block', textDecoration: 'none', minWidth: 120, textAlign: 'center' }}
                >
                  {t('shop.authCodePageGoManage')}
                </Link>
              </>
            ) : (
              <>
                <div
                  role="radiogroup"
                  aria-label={t('shop.authCodePageSelectLabel')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginBottom: 20,
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  {unauthorizedShops.map((shop) => {
                    const id = String(shop.id);
                    const checked = selectedShopId === id;
                    const typeText = shopTypeLabel(shop.shopType, shop.platformCode) || shop.shopType || '';
                    return (
                      <label
                        key={id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: '12px 14px',
                          borderRadius: 8,
                          border: checked
                            ? '1px solid var(--accent, #2563eb)'
                            : '1px solid var(--border, #ddd)',
                          background: checked ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
                          cursor: submitting ? 'default' : 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="tiktok-oauth-target-shop"
                          value={id}
                          checked={checked}
                          disabled={submitting}
                          onChange={() => setSelectedShopId(id)}
                          style={{ marginTop: 3 }}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600, lineHeight: 1.4 }}>
                            {shopDisplayName(shop)}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted, #666)', marginTop: 4 }}>
                            {typeText
                              ? `${typeText} · ID ${id}`
                              : `ID ${id}`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="funding-button funding-button--primary"
                  onClick={onConfirm}
                  disabled={submitting || !selectedShopId}
                  style={{ minWidth: 160 }}
                >
                  {submitting ? t('shop.authCodePageBinding') : t('shop.authCodePageConfirm')}
                </button>
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
